import { translate } from '@fiverr-private/i18n-react';

const MIN_LENGTH = 3;
const MAX_LENGTH = 15;
const TRANSLATION_PREFIX = 'seller_profile.display_name.validations';
const FIRST_CHARACTER_LETTER_REGEX = /^[A-Za-z].*$/;
const ALLOWED_CHARACTERS_REGEX = /^[A-Za-z\s.'-]+$/;

const enum ValidationErrors {
    MandatoryField = 'MandatoryField',
    MinLength = 'MinLength',
    MaxLength = 'MaxLength',
    FirstCharacterLetter = 'FirstCharacterLetter',
    AllowedCharacters = 'AllowedCharacters',
}

export interface ValidationResult {
    isValid: boolean;
    errorCode?: ValidationErrors;
    errorMessage?: string;
}

/**
 * Validates the given display name based on some criteria.
 * @param displayName - The display name to validate.
 * @returns An object containing a boolean indicating whether the validation passed or not,
 * and optionally, an error code and error message if validation failed.
 */
export const validate = (displayName: string): ValidationResult => {
    if (!displayName) {
        return {
            isValid: false,
            errorCode: ValidationErrors.MandatoryField,
            errorMessage: translate(`${TRANSLATION_PREFIX}.mandatory_field`),
        };
    }

    displayName = displayName.trim();

    if (displayName.length < MIN_LENGTH) {
        return {
            isValid: false,
            errorCode: ValidationErrors.MinLength,
            errorMessage: translate(`${TRANSLATION_PREFIX}.min_length`, {
                params: { minLength: MIN_LENGTH },
            }),
        };
    }

    if (displayName.length > MAX_LENGTH) {
        return {
            isValid: false,
            errorCode: ValidationErrors.MaxLength,
            errorMessage: translate(`${TRANSLATION_PREFIX}.max_length`, {
                params: { maxLength: MAX_LENGTH },
            }),
        };
    }

    if (!FIRST_CHARACTER_LETTER_REGEX.test(displayName)) {
        return {
            isValid: false,
            errorCode: ValidationErrors.FirstCharacterLetter,
            errorMessage: translate(`${TRANSLATION_PREFIX}.first_character_letter`),
        };
    }

    if (!ALLOWED_CHARACTERS_REGEX.test(displayName)) {
        return {
            isValid: false,
            errorCode: ValidationErrors.AllowedCharacters,
            errorMessage: translate(`${TRANSLATION_PREFIX}.allowed_characters`),
        };
    }

    return {
        isValid: true,
    };
};
